import { Link } from "react-router"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEnvelope, faFileLines } from "@fortawesome/pro-solid-svg-icons"
import { DateTime } from "luxon"

import WizardHeader from "../../components/WizardHeader"
import WizardCard from "../../components/WizardCard"
import CheckmarkListItem from "../../components/CheckmarkListItem"
import WizardFooter from "../../components/WizardFooter"
import TextPair from "./TextPair"
import { formatCurrency } from "../../utils"
import {
  LiabilityInsuranceTypes,
  SelectedImplementationType,
} from "@/types/liabilityInsuranceWizard"

export interface LiabilityInsuranceConfirmationProps {
  insuranceWizardData: LiabilityInsuranceTypes
}

const COVERAGE_TYPE_LABELS: Record<
  NonNullable<SelectedImplementationType>,
  string
> = {
  ins_general_liability: "Comprehensive General Liability",
  ins_hunting_liability: "Hunting Lease Liability",
  "ins_general+hunting_liability":
    "Comprehensive General and Hunting Lease Liability",
}

const LiabilityInsuranceConfirmation = ({
  insuranceWizardData,
}: LiabilityInsuranceConfirmationProps) => {
  const hasGeneralLiability =
    insuranceWizardData?.selected_implementation ===
      "ins_general+hunting_liability" ||
    insuranceWizardData?.selected_implementation === "ins_general_liability"

  const hasHuntingLiability =
    insuranceWizardData?.selected_implementation ===
      "ins_general+hunting_liability" ||
    insuranceWizardData?.selected_implementation === "ins_hunting_liability"

  const generalPremium = hasGeneralLiability
    ? insuranceWizardData?.general_liability_price_dollars
    : 0
  const huntingPremium = hasHuntingLiability
    ? insuranceWizardData?.hunting_liability_price_dollars
    : 0
  const totalPremium = generalPremium + huntingPremium

  const coverageTypeLabel =
    COVERAGE_TYPE_LABELS[
      insuranceWizardData?.selected_implementation ?? "ins_general_liability"
    ]

  const { start_date, end_date } =
    insuranceWizardData?.current_coverage_period ?? {}

  const formattedStart = start_date
    ? DateTime.fromFormat(start_date, "LLLL dd, yyyy").toFormat("MM/dd/yyyy")
    : ""

  const formattedEnd = end_date
    ? DateTime.fromFormat(end_date, "LLLL dd, yyyy").toFormat("MM/dd/yyyy")
    : ""

  const coveragePeriodText = `${formattedStart} - ${formattedEnd}`

  return (
    <div
      className="pt-10 lg:pt-13 pb-31"
      data-testid="liability-insurance-confirmation"
    >
      <WizardHeader
        heading="Congratulations, your policy is bound!"
        text="Outdoor Underwriters will review your application and you will receive your certificate of insurance within 2-3 business days."
      />

      <div className="container w-[752px] max-w-full flex flex-col gap-6 px-5 lg:px-10 mt-10">
        <WizardCard paddingSize="sm">
          <div className="flex flex-col gap-6">
            <h2
              className="text-2xl font-display font-medium leading-[124%] tracking-[0.48px]"
              data-testid="policy-details-heading"
            >
              Policy Details
            </h2>

            <div className="flex flex-col gap-4">
              <TextPair
                leftText="Coverage Type:"
                rightText={coverageTypeLabel}
                testId="coverage-type"
              />

              <TextPair
                leftText="Coverage Period:"
                rightText={coveragePeriodText}
                testId="coverage-period"
              />

              <TextPair
                leftText="Total Annual Premium:"
                rightText={formatCurrency(totalPremium)}
                testId="total-annual-premium"
              />

              <TextPair
                leftText="Master Policy Document:"
                rightText={
                  <a
                    href="https://ncx-platform-public.s3.us-east-1.amazonaws.com/insurance/Timberland_Sample_Master_Policy.pdf"
                    className="link"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FontAwesomeIcon icon={faFileLines} className="mr-2" />
                    View Document
                  </a>
                }
                testId="master-policy-document"
              />
            </div>
          </div>
        </WizardCard>

        <WizardCard paddingSize="sm">
          <h2
            className="text-2xl font-display font-medium leading-[124%] tracking-[0.48px]"
            data-testid="important-information-heading"
          >
            Important Information
          </h2>

          <ul className="grid grid-cols-1 gap-5 mt-[26px] mb-4">
            <CheckmarkListItem>
              Your coverage is not effective until you receive your certificate
            </CheckmarkListItem>

            <CheckmarkListItem>
              Keep your certificate of insurance accessible and share it with
              family members
            </CheckmarkListItem>

            <CheckmarkListItem>
              Contact NCX support if you need to make changes to your policy or
              file a claim
            </CheckmarkListItem>

            <CheckmarkListItem>
              Your policy will not be automatically renewed. We will be in touch
              near the end of your policy period to discuss options for renewal.
            </CheckmarkListItem>
          </ul>
        </WizardCard>

        <WizardCard paddingSize="sm">
          <div className="flex flex-col gap-6">
            <h2
              className="text-2xl font-display font-medium leading-[124%] tracking-[0.48px]"
              data-testid="hunting-lease-heading"
            >
              Interested in Hunting Lease Liability Insurance?
            </h2>

            <p
              className="text-charcoal-500 text-base font-body leading-[140%] tracking-[0.32px]"
              data-testid="hunting-lease-text"
            >
              If you're considering adding hunting lease liability coverage to
              your property insurance, our specialists can help you explore your
              options and provide a customized quote.
            </p>

            <p
              className="text-charcoal-500 text-base font-body leading-[140%] tracking-[0.32px]"
              data-testid="hunting-lease-email"
            >
              <FontAwesomeIcon icon={faEnvelope} className="text-leaf mr-2" />
              Contact us at:
              <a
                href="mailto:landowners@ncx.com"
                className="link ml-2"
                target="_blank"
                rel="noreferrer"
              >
                landowners@ncx.com
              </a>
            </p>
          </div>
        </WizardCard>
      </div>

      <WizardFooter>
        <div className="flex gap-4 ml-auto">
          <Link
            to="/"
            className="liability-insurance-confirmation-dashboard-link btn2 btn2-primary font-bold tracking-[0.32px] whitespace-nowrap"
            data-testid="dashboard-link"
          >
            Take me to my Dashboard
          </Link>
        </div>
      </WizardFooter>
    </div>
  )
}

export default LiabilityInsuranceConfirmation
